import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "../../components/Icon";
import { getUpcomingEvents } from "../../lib/api-event";
import type { FamilyEvent, UpcomingEvents } from "../../types/event";

type UpcomingState =
  | { status: "loading" }
  | { status: "loaded"; data: UpcomingEvents }
  | { status: "error" };

export function UpcomingEventsPanel() {
  const navigate = useNavigate();
  const [retryKey, setRetryKey] = useState(0);
  const [state, setState] = useState<UpcomingState>({ status: "loading" });

  useEffect(() => {
    let ignore = false;
    setState({ status: "loading" });
    void getUpcomingEvents().then(
      (data) => {
        if (!ignore) setState({ status: "loaded", data });
      },
      () => {
        if (!ignore) setState({ status: "error" });
      },
    );
    return () => {
      ignore = true;
    };
  }, [retryKey]);

  if (state.status === "loading") {
    return (
      <section aria-label="Загрузка ближайших событий" className="mb-8 rounded-[26px] bg-white p-5 shadow-[0_12px_32px_rgba(35,33,25,0.06)]">
        <div className="h-5 w-44 animate-pulse rounded-full bg-[#E7E5DE]" />
        <div className="mt-4 h-16 animate-pulse rounded-2xl bg-[#F2F1EC]" />
      </section>
    );
  }

  if (state.status === "error") {
    return (
      <section className="mb-8 flex flex-wrap items-center justify-between gap-4 rounded-[26px] border border-[#F4C48F] bg-[#FFF7ED] p-5">
        <div>
          <h2 className="font-extrabold text-[#171714]">События временно недоступны</h2>
          <p className="mt-1 text-sm text-[#77776F]">Семейные списки ниже продолжают работать.</p>
        </div>
        <button
          type="button"
          onClick={() => setRetryKey((key) => key + 1)}
          className="rounded-full bg-[#171714] px-5 py-2.5 text-sm font-bold text-white"
        >
          Повторить
        </button>
      </section>
    );
  }

  const todayEvents = state.data.events.filter((event) => event.date === state.data.today);
  const upcomingEvents = state.data.events.filter((event) => event.date !== state.data.today);

  if (state.data.events.length === 0) {
    return (
      <section className="mb-8 flex items-center justify-between gap-4 rounded-[26px] bg-white p-5 shadow-[0_12px_32px_rgba(35,33,25,0.06)]">
        <div>
          <h2 className="font-extrabold text-[#171714]">Ближайших событий пока нет</h2>
          <p className="mt-1 text-sm text-[#77776F]">Добавьте семейный повод, когда будете готовы.</p>
        </div>
        <button type="button" onClick={() => navigate("/events")} aria-label="Открыть события" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#FFF0DF] text-[#FF7A00]">
          <Icon name="calendar" size={20} />
        </button>
      </section>
    );
  }

  return (
    <section className="mb-8 space-y-6" aria-label="Ближайшие семейные события">
      {todayEvents.length > 0 && (
        <div className="rounded-[28px] bg-[#171714] p-5 text-white shadow-[0_16px_38px_rgba(23,23,20,0.2)] sm:p-6">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#FFB36B]">Сегодня</p>
          <div className="mt-4 grid gap-3 lg:grid-cols-2">
            {todayEvents.map((event) => (
              <DashboardEventCard key={event.id} event={event} today onOpenWishlist={(memberID) => navigate(`/members/${memberID}/wishes`)} />
            ))}
          </div>
        </div>
      )}

      {upcomingEvents.length > 0 && (
        <div>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-xl font-extrabold tracking-[-0.035em] text-[#171714]">Ближайшие 30 дней</h2>
            <button type="button" onClick={() => navigate("/events")} className="text-sm font-bold text-[#C45D00]">Все события</button>
          </div>
          <ol className="grid gap-3 lg:grid-cols-2">
            {upcomingEvents.map((event) => (
              <DashboardEventCard key={event.id} event={event} onOpenWishlist={(memberID) => navigate(`/members/${memberID}/wishes`)} />
            ))}
          </ol>
        </div>
      )}
    </section>
  );
}

interface DashboardEventCardProps {
  event: FamilyEvent;
  today?: boolean;
  onOpenWishlist: (memberID: string) => void;
}

function DashboardEventCard({ event, today = false, onOpenWishlist }: DashboardEventCardProps) {
  return (
    <article className={`rounded-2xl p-4 ${today ? "bg-white/10" : "bg-white shadow-[0_10px_28px_rgba(35,33,25,0.06)]"}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className={`font-extrabold ${today ? "text-white" : "text-[#171714]"}`}>{event.title}</h3>
          <p className={`mt-1 text-sm ${today ? "text-white/65" : "text-[#77776F]"}`}>{formatEventDate(event.date)}</p>
        </div>
        <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.08em] ${today ? "bg-white/10 text-[#FFD2A8]" : "bg-[#FFF0DF] text-[#C45D00]"}`}>
          {eventTypeLabel[event.type]}
        </span>
      </div>
      {event.member && (
        <button
          type="button"
          onClick={() => onOpenWishlist(event.member!.id)}
          className={`mt-4 inline-flex items-center gap-2 text-sm font-bold ${today ? "text-[#FFD2A8]" : "text-[#C45D00]"}`}
        >
          Список желаний · {event.member.name}
        </button>
      )}
    </article>
  );
}

const eventTypeLabel: Record<FamilyEvent["type"], string> = {
  birthday: "День рождения",
  anniversary: "Годовщина",
  holiday: "Праздник",
  other: "Событие",
};

function formatEventDate(date: string): string {
  return new Date(`${date}T00:00:00`).toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
  });
}
